import React, { useState, useMemo } from 'react';
import type { Parcel } from '../data/useParcels';
import { T } from '../theme/tokens';
import { LOT, UNIT, PERMIT } from '../ids';
import { Btn } from '../ui/primitives';
import { parseDate } from '../util/date';
const fmt = (n: number) => n.toLocaleString('en-US');

// Filter group → backing backend field ID, so a rename flows into the filter sub-header label.
const FIELD_OF: Record<string, string> = {
  phase: LOT.masterPhase, subPhase: LOT.subPhase, concurrency: UNIT.concurrency, landUse: LOT.landUseCategory,
  // contractStatus filter: DATA reads the live field (SEL below); the sub-header LABEL tracks the
  // canonical "Contract Status" name so it reads clean, not "Contract Status (Live)".
  buildStatusRaw: UNIT.buildStageV5, contractStatus: UNIT.contractStatus,
  model: UNIT.model,
  version: UNIT.unitVersion, modelVersion: UNIT.unitModelVersion, pkg: UNIT.unitPackage, orientation: UNIT.houseOrientation, county: LOT.countyClarificationType,
  openClosed: PERMIT.openClosed,
};

export interface FilterState {
  phase: Set<string>; subPhase: Set<string>; concurrency: Set<string>; landUse: Set<string>;
  sales: Set<string>; build: Set<string>; buildStatusRaw: Set<string>;
  contractStatus: Set<string>; constructionUpgrades: Set<string>;
  escrowStatus: Set<string>; escrowReceived: Set<string>;
  financialIncentives: Set<string>; jobContingency: Set<string>; additionalContingencies: Set<string>;
  model: Set<string>; tract: Set<string>; version: Set<string>; modelVersion: Set<string>; pkg: Set<string>; orientation: Set<string>; county: Set<string>;
  permitIssued: Set<string>;
  inspState: Set<string>; schedule: Set<string>; urgency: Set<string>; nextInsp: Set<string>; closeout: Set<string>; openClosed: Set<string>;
  block: number | null; lot: number | null;  // exact-match Block #/Lot # search values (separate)
  buildOrderMin: number | null; buildOrderMax: number | null;  // inclusive global build-order range — set by clicking a Build Order heat band (filters to that band's lots)
  lakeviewOnly: boolean; sidewalkOnly: boolean; easementOnly: boolean; markersOnly: boolean; onlyExceptions: boolean;
  sfMin: number | null; sfMax: number | null;
}
export const emptyFilters = (): FilterState => ({
  phase: new Set(), subPhase: new Set(), concurrency: new Set(), landUse: new Set(),
  sales: new Set(), build: new Set(), buildStatusRaw: new Set(),
  contractStatus: new Set(), constructionUpgrades: new Set(),
  escrowStatus: new Set(), escrowReceived: new Set(),
  financialIncentives: new Set(), jobContingency: new Set(), additionalContingencies: new Set(),
  model: new Set(), tract: new Set(), version: new Set(), modelVersion: new Set(), pkg: new Set(), orientation: new Set(), county: new Set(),
  permitIssued: new Set(), inspState: new Set(), schedule: new Set(), urgency: new Set(), nextInsp: new Set(), closeout: new Set(), openClosed: new Set(),
  block: null, lot: null, buildOrderMin: null, buildOrderMax: null,
  lakeviewOnly: false, sidewalkOnly: false, easementOnly: false, markersOnly: false, onlyExceptions: false, sfMin: null, sfMax: null,
});

/** Saved-view (de)serialization — STRUCTURAL over FilterState (Sets → arrays, scalars verbatim),
 *  so a newly added filter group can never be silently dropped by a stale hand-kept key list
 *  (the old LeftRail SETKEYS missed `permitIssued`, and saved views lost that group on save).
 *  deser walks emptyFilters() — unknown stored keys are ignored, missing keys default cleanly,
 *  and tri-state '!'-prefixed exclusions ride through untouched (they're plain Set members). */
export const serFilters = (f: FilterState): any => {
  const o: any = {};
  for (const [k, v] of Object.entries(f)) o[k] = v instanceof Set ? Array.from(v) : v;
  return o;
};
export const deserFilters = (o: any): FilterState => {
  const f = emptyFilters(); if (!o) return f;
  for (const k of Object.keys(f) as (keyof FilterState)[]) {
    const blank = f[k];
    if (blank instanceof Set) (f[k] as any) = new Set(Array.isArray(o[k]) ? o[k].filter((v: any) => typeof v === 'string') : []);
    else if (typeof blank === 'boolean') (f[k] as any) = !!o[k];
    else (f[k] as any) = typeof o[k] === 'number' ? o[k] : null; // sfMin/sfMax
  }
  return f;
};

/** Display form for one stored chip value — tri-state exclusions ('!x') read as "≠ x". */
export const chipText = (v: string): string => {
  // Blank field value (the "(none)" / "—" breakdown buckets store '') gets a readable label rather
  // than an empty chip; exclusions ('!x') read "≠ x", and "≠ (none)" for an excluded blank.
  if (v.startsWith('!')) { const x = v.slice(1); return `≠ ${x === '' ? '(none)' : x}`; }
  return v === '' ? '(none)' : v;
};

const SEL: Record<string, (p: Parcel) => string> = {
  phase: p => p.masterPhase, subPhase: p => p.subPhase, concurrency: p => p.unit?.concurrency || '',
  landUse: p => p.landUseCategory || p.geo.designation,
  sales: p => p.salesBucket,
  // Live contract status (GHL lookup else synced Contract Status) — same field the Color By + grid use.
  contractStatus: p => p.unit?.contractStatusLive || '',
  constructionUpgrades: p => p.constructionUpgrade || '',
  escrowStatus: p => p.escrowStatus || '',
  // Yes/No cut on whether escrow funds have landed — derived from the received DATE. Gated on escrow
  // activity: a lot with no escrow status returns '' so it's excluded from BOTH Yes and No (it isn't
  // in the escrow pipeline at all), rather than masquerading as a "No".
  escrowReceived: p => p.escrowReceivedDate ? 'Yes' : (p.escrowStatus ? 'No' : ''),
  financialIncentives: p => p.financialIncentives || '',
  jobContingency: p => p.jobContingency || '',
  additionalContingencies: p => p.additionalContingencies || '',
  build: p => p.buildPhase, buildStatusRaw: p => p.unit?.buildStatus || '',  // granular Unit Build Status
  model: p => p.allModels || p.model,         // multi-unit lots list every model (tokenized)
  tract: p => p.unit ? '' : p.model,          // (unused — kept for saved-view compatibility)
  version: p => p.unit?.version || '', modelVersion: p => p.unit?.modelVersion || '', pkg: p => p.unit?.pkg || '', orientation: p => p.unit?.orientation || '',
  county: p => p.countyClarType,
  permitIssued: p => p.permitIssued,
  // Inspection status = the LATEST inspection PER TYPE (not "ever"). A lot matches a status if the
  // most recent inspection of SOME type is currently in it — so a framing that failed then passed
  // drops off "Failed", and "Cancelled" surfaces a type whose latest visit was cancelled with
  // nothing newer. This mirrors the normalizer's resolved-by-later-same-type-pass model. Full
  // history (every inspection) stays available in the Inspections grid tab.
  inspState: p => {
    // Compare as parsed epochs, NOT raw strings: sortDate mixes ISO ("2026-06-12") and US
    // ("6/9/2026") renderings, so a lexicographic >= picks the wrong "latest" (e.g. "6/9" sorts
    // after "10/1"). Every other date compare in the app parses first — this one was missed.
    const latestByType = new Map<string, { d: number; status: string }>();
    for (const i of p.inspections) {
      const t = (i.type || '').trim().toLowerCase(); if (!t) continue;
      const status = (i.bucket || i.result || '').trim(); if (!status) continue;
      const d = parseDate(i.sortDate || '');
      const prev = latestByType.get(t);
      if (!prev || d >= prev.d) latestByType.set(t, { d, status });
    }
    return [...new Set([...latestByType.values()].map(v => v.status))].join(',');
  },
  schedule: p => p.inspStatus || '',                                  // Scheduled / Overdue / Blind / Expired …
  urgency: p => (p.urgencyBand || '').replace(/^\d+\s*/, '') || '',   // Critical / Overdue / Schedule needed / Monitor
  nextInsp: p => p.nextInspection || '',                              // the next expected inspection type
  closeout: p => p.closeoutStage || '',                              // % to CO ladder stage
  openClosed: p => p.openClosed || (p.permitNumber ? 'Open' : ''),   // Open / Open / Superseded / Completed / Closed
};

// Multi-value fields hold comma-joined lists (e.g. "Closing Costs Contribution, Appliance Package").
// For these we tokenize: each distinct value is its own chip, and a lot matches if it contains ANY
// selected value — instead of one chip per unreadable full combination.
const MULTI = new Set(['constructionUpgrades', 'financialIncentives', 'jobContingency', 'additionalContingencies', 'model', 'inspState']);
// GHL category fields are newline-separated (values can contain internal commas, e.g.
// "Attic - Finished (2BR, Bath, Staircase)"). model and inspState stay comma-separated.
const NEWLINE_KEYS = new Set(['constructionUpgrades', 'financialIncentives', 'jobContingency', 'additionalContingencies']);
const tokens = (s: string, key?: string) => s.split(key && NEWLINE_KEYS.has(key) ? '\n' : ',').map(x => x.trim()).filter(Boolean);

// Values pinned to the FRONT of a filter's chip list, in this order, ahead of the frequency sort.
const PRIORITY: Record<string, string[]> = {
  landUse: ['Residential', 'Commercial', 'Institutional'],
};
// Values pushed to the END of a filter's chip list (placeholders / catch-alls), regardless of count.
const DEMOTE = new Set(['Residential (Unplatted Placeholder)']);
// Filter groups sorted strictly A→Z (numbers/letters) instead of by frequency.
const ALPHA = new Set(['subPhase', 'phase', 'concurrency']);

export function filterCount(f: FilterState): number {
  let n = 0;
  (Object.keys(SEL) as (keyof FilterState)[]).forEach(k => { n += (f[k] as Set<string>).size; });
  if (f.lakeviewOnly) n++; if (f.sidewalkOnly) n++; if (f.easementOnly) n++; if (f.markersOnly) n++; if (f.onlyExceptions) n++;
  if (f.sfMin != null || f.sfMax != null) n++;
  if (f.block != null) n++;
  if (f.lot != null) n++;
  if (f.buildOrderMin != null || f.buildOrderMax != null) n++;
  return n;
}

/** Human label for the build-order range chip — "Build order #12–#44" / "≥ #12" / "≤ #44". */
export const buildOrderRangeLabel = (lo: number | null, hi: number | null): string =>
  lo != null && hi != null ? `Build order #${lo}–#${hi}` : lo != null ? `Build order ≥ #${lo}` : `Build order ≤ #${hi}`;

// Human-readable summary of the active filter criteria — e.g. "Unit package: Basic  ·  Lakeview
// only  ·  Lot SF 8,000–12,000". Used by the PDF export so a filtered view documents what produced
// it. Prefers the live backend field name (so a rename flows through) over the curated label.
export function describeFilters(f: FilterState, fieldNames?: Record<string, string>): string {
  const label = (key: string) => (FIELD_OF[key] && fieldNames && fieldNames[FIELD_OF[key]]) || GROUP_LABEL[key] || key;
  const parts: string[] = [];
  for (const key of Object.keys(SEL)) {
    const set = f[key as keyof FilterState] as Set<string>;
    if (set && set.size) parts.push(`${label(key)}: ${[...set].map(chipText).join(', ')}`);
  }
  if (f.lakeviewOnly) parts.push('Lakeview only');
  if (f.sidewalkOnly) parts.push('Sidewalk only');
  if (f.easementOnly) parts.push('Easement only');
  if (f.markersOnly) parts.push('Has site markers');
  if (f.onlyExceptions) parts.push('Exceptions only');
  if (f.sfMin != null || f.sfMax != null) {
    const lo = f.sfMin != null ? fmt(f.sfMin) : null, hi = f.sfMax != null ? fmt(f.sfMax) : null;
    parts.push(lo && hi ? `Lot SF ${lo}–${hi}` : lo ? `Lot SF ≥ ${lo}` : `Lot SF ≤ ${hi}`);
  }
  if (f.block != null) parts.push(`Block # ${f.block}`);
  if (f.lot != null) parts.push(`Lot # ${f.lot}`);
  if (f.buildOrderMin != null || f.buildOrderMax != null) parts.push(buildOrderRangeLabel(f.buildOrderMin, f.buildOrderMax));
  return parts.join('  ·  ');
}

export function applyFilters(parcels: Parcel[], f: FilterState): Set<string> {
  const out = new Set<string>();
  for (const p of parcels) {
    let ok = true;
    for (const key of Object.keys(SEL)) {
      const set = f[key as keyof FilterState] as Set<string>;
      if (!set.size) continue;
      const inc = new Set([...set].filter(x => !x.startsWith('!')));
      const exc = new Set([...set].filter(x => x.startsWith('!')).map(x => x.slice(1)));
      const val = SEL[key](p);
      if (MULTI.has(key)) {
        const tk = tokens(val, key);
        if (inc.size && !tk.some(t => inc.has(t))) { ok = false; break; }
        if (exc.size && tk.some(t => exc.has(t))) { ok = false; break; }
      } else {
        if (inc.size && !inc.has(val)) { ok = false; break; }
        if (exc.size && exc.has(val)) { ok = false; break; }
      }
    }
    if (ok && f.lakeviewOnly && !p.lakeview) ok = false;
    if (ok && f.sidewalkOnly && !p.sidewalk) ok = false;
    if (ok && f.easementOnly && !p.easement) ok = false;
    if (ok && f.markersOnly && !p.hasSiteMarkers) ok = false;
    if (ok && f.onlyExceptions && !p.isException) ok = false;
    if (ok && f.sfMin != null && (p.lotSizeSF == null || p.lotSizeSF < f.sfMin)) ok = false;
    if (ok && f.sfMax != null && (p.lotSizeSF == null || p.lotSizeSF > f.sfMax)) ok = false;
    if (ok && f.block != null && p.block !== f.block) ok = false;
    if (ok && f.lot != null && p.lot !== f.lot) ok = false;
    if (ok && f.buildOrderMin != null && (p.buildOrder == null || p.buildOrder < f.buildOrderMin)) ok = false;
    if (ok && f.buildOrderMax != null && (p.buildOrder == null || p.buildOrder > f.buildOrderMax)) ok = false;
    if (!ok) out.add(p.id);
  }
  return out;
}

const GROUP_LABEL: Record<string, string> = {
  phase: 'Phase', subPhase: 'Sub-phase', concurrency: 'Concurrency phase', landUse: 'Land Use',
  sales: 'Sales bucket', build: 'Master build stage', buildStatusRaw: 'Build stage',
  contractStatus: 'Contract status', constructionUpgrades: 'Construction upgrades',
  escrowStatus: 'Escrow status', escrowReceived: 'Escrow received',
  financialIncentives: 'Financial incentives', jobContingency: 'Job contingency', additionalContingencies: 'Additional contingencies',
  permitIssued: 'Permit issued', inspState: 'Inspection status', schedule: 'Schedule status', urgency: 'Urgency', nextInsp: 'Next inspection', closeout: 'Closeout stage', openClosed: 'Open / Closed',
  model: 'Model', tract: 'Tract / facility', version: 'Succo Version', modelVersion: 'Unit Model Version', pkg: 'Unit package', orientation: 'House orientation', county: 'County clarification',
};
// Per-group clarifying microcopy (rendered under the group label). Keeps the Sale-status buckets
// unambiguous now that the sync defines Sold = escrow-funded only (Countersigned → Sale Pending).
const GROUP_HINT: Record<string, string> = {
  version: 'Per-model spec revision — the letter is the model family, the number is the revision (e.g. E-3, R-1).',
  build: 'The coarse construction lifecycle bucket each unit rolls up to (Not started → Foundation → Dry-in → Finishes → Done).',
  buildStatusRaw: 'The current Build Stage (v5) construction step (Not started → Foundation → Shell → Dry-in → Interior Rough → Drywall → Finishes → Complete).',
  escrowReceived: 'Yes = escrow funds received; No = in escrow but not yet funded. Only lots with escrow activity appear.',
};
// `subheads` renders a small cluster divider above the named key — so one collapsible section can
// hold several related groups without a wall of equal-weight labels (e.g. all the GHL deal terms
// sit under one "Sales" toggle, with an "Incentives & contingencies" divider grouping the trio).
const SECTIONS: { title: string; keys: (keyof FilterState)[]; open?: boolean; subheads?: Partial<Record<keyof FilterState, string>> }[] = [
  { title: 'Phase & land', keys: ['phase', 'subPhase', 'concurrency', 'landUse'], open: true },
  { title: 'Product', keys: ['model', 'pkg', 'version', 'modelVersion', 'orientation'] },
  // ONE consolidated Sales toggle: pipeline status → escrow → contractual upgrades → incentives. The
  // upgrade + incentive groups (formerly their own top-level toggles) fold in here; each group still
  // renders only once the GHL sync populates its field.
  { title: 'Sales',
    keys: ['contractStatus', 'escrowStatus', 'escrowReceived', 'constructionUpgrades', 'financialIncentives', 'jobContingency', 'additionalContingencies'],
    subheads: { financialIncentives: 'Incentives & contingencies' } },
  // Construction state, lifted out of Sales into its own toggle. Holds BOTH build dimensions so they
  // read as one mental model: "Master build stage" (the coarse lifecycle bucket — Not started →
  // Foundation → Dry-in → Finishes → Done) over "Build stage" (the granular Build Stage v5 step).
  { title: 'Construction', keys: ['build', 'buildStatusRaw'] },
  // Permit lifecycle on the MAP — "show me what's issued vs still waiting on the county".
  // 'openClosed' rail filter removed 2026-06-27 — redundant with Permit issued / permit status.
  { title: 'Permits', keys: ['permitIssued', 'urgency', 'inspState', 'schedule', 'nextInsp', 'closeout'] },
];

/** Unified "active criteria" bar — ONE dismissible surface for every mechanism that can hide
 *  lots (left-rail filter sets, boolean toggles, SF range, right-panel isolation). Renders over
 *  the map so the user can always see and clear WHY the view is narrowed, from anywhere. */
export function CriteriaBar({ filters, setFilters, isolated, onClearIsolation, fieldNames }: {
  filters: FilterState; setFilters: (f: FilterState) => void;
  isolated: Set<string>; onClearIsolation: () => void; fieldNames?: Record<string, string>;
}) {
  const label = (key: string) => (FIELD_OF[key] && fieldNames && fieldNames[FIELD_OF[key]]) || GROUP_LABEL[key] || key;
  const chips: { text: string; clear: () => void }[] = [];
  for (const key of Object.keys(SEL)) {
    const set = filters[key as keyof FilterState] as Set<string>;
    if (!set || !set.size) continue;
    // chipText: tri-state exclusions stored as '!x' must read "≠ x" here too, not raw "!x"
    const vals = [...set].map(chipText); const shown = vals.slice(0, 3).join(', ') + (vals.length > 3 ? ` +${vals.length - 3}` : '');
    chips.push({ text: `${label(key)}: ${shown}`, clear: () => setFilters({ ...filters, [key]: new Set() }) });
  }
  if (filters.lakeviewOnly) chips.push({ text: 'Lakeview only', clear: () => setFilters({ ...filters, lakeviewOnly: false }) });
  if (filters.sidewalkOnly) chips.push({ text: 'Sidewalk only', clear: () => setFilters({ ...filters, sidewalkOnly: false }) });
  if (filters.easementOnly) chips.push({ text: 'Easement only', clear: () => setFilters({ ...filters, easementOnly: false }) });
  if (filters.markersOnly) chips.push({ text: 'Has site markers', clear: () => setFilters({ ...filters, markersOnly: false }) });
  if (filters.onlyExceptions) chips.push({ text: 'Exceptions only', clear: () => setFilters({ ...filters, onlyExceptions: false }) });
  if (filters.sfMin != null || filters.sfMax != null) {
    const lo = filters.sfMin != null ? fmt(filters.sfMin) : null, hi = filters.sfMax != null ? fmt(filters.sfMax) : null;
    chips.push({ text: lo && hi ? `Lot SF ${lo}–${hi}` : lo ? `Lot SF ≥ ${lo}` : `Lot SF ≤ ${hi}`,
      clear: () => setFilters({ ...filters, sfMin: null, sfMax: null }) });
  }
  if (filters.block != null) chips.push({ text: `Block # ${filters.block}`, clear: () => setFilters({ ...filters, block: null }) });
  if (filters.lot != null) chips.push({ text: `Lot # ${filters.lot}`, clear: () => setFilters({ ...filters, lot: null }) });
  if (filters.buildOrderMin != null || filters.buildOrderMax != null) chips.push({ text: buildOrderRangeLabel(filters.buildOrderMin, filters.buildOrderMax), clear: () => setFilters({ ...filters, buildOrderMin: null, buildOrderMax: null }) });
  if (isolated.size > 0) {
    const vals = [...isolated]; const shown = vals.slice(0, 3).join(', ') + (vals.length > 3 ? ` +${vals.length - 3}` : '');
    chips.push({ text: `Isolated: ${shown}`, clear: onClearIsolation });
  }
  if (!chips.length) return null;
  const clearAll = () => { setFilters(emptyFilters()); onClearIsolation(); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', maxWidth: 720, justifyContent: 'center' }}>
      {chips.map((c, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.paper, border: `1px solid ${T.line}`,
          borderRadius: 999, padding: '3px 5px 3px 10px', fontSize: 11, color: T.ink, boxShadow: T.shadow, maxWidth: 320 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.text}</span>
          <button onClick={c.clear} className="lfe-press" aria-label={`Clear ${c.text}`}
            style={{ width: 16, height: 16, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center',
              background: T.sand2, color: T.inkSoft, fontSize: 10, lineHeight: 1, padding: 0 }}>✕</button>
        </span>
      ))}
      {chips.length > 1 && (
        <button onClick={clearAll} className="lfe-press"
          style={{ border: 'none', background: T.ink, color: T.onInk, borderRadius: 999, padding: '4px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', boxShadow: T.shadow }}>
          Clear all
        </button>
      )}
    </div>
  );
}

export default function Filters({ parcels, filters, setFilters, fieldNames }:
  { parcels: Parcel[]; filters: FilterState; setFilters: (f: FilterState) => void; fieldNames?: Record<string, string> }) {
  // Sub-header label: live backend field name when known, else the curated fallback.
  const groupLabel = (key: string) => (FIELD_OF[key] && fieldNames && fieldNames[FIELD_OF[key]]) || GROUP_LABEL[key];
  const vals = (key: string) => {
    const raw = parcels.map(SEL[key]).filter(Boolean);
    const arr = MULTI.has(key) ? raw.flatMap(s => tokens(s, key)) : raw;  // individual upgrade options, not combos
    const count = new Map<string, number>();
    for (const v of arr) count.set(v, (count.get(v) || 0) + 1);
    const pri = PRIORITY[key];  // pinned-first values (e.g. Land Use → Residential, Commercial, Institutional)
    const rank = (v: string) => { if (DEMOTE.has(v)) return 1e6; const i = pri ? pri.indexOf(v) : -1; return i < 0 ? pri ? pri.length : 0 : i; };
    if (ALPHA.has(key)) return [...count.keys()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })); // strict A→Z / 1→9
    // pinned order first, then most common, ties alphabetical, demoted placeholders last
    return [...count.keys()].sort((a, b) => rank(a) - rank(b) || count.get(b)! - count.get(a)! || a.localeCompare(b));
  };
  // Lot-SF bounds over parcels passing every OTHER filter — so the min/max placeholders adapt to
  // the current selection (e.g. filter to Residential → the SF range shows that subset's span).
  const sfBounds = useMemo(() => {
    const excl = applyFilters(parcels, { ...filters, sfMin: null, sfMax: null });
    let mn = Infinity, mx = -Infinity;
    for (const p of parcels) { if (excl.has(p.id) || p.lotSizeSF == null) continue; if (p.lotSizeSF < mn) mn = p.lotSizeSF; if (p.lotSizeSF > mx) mx = p.lotSizeSF; }
    return mn === Infinity ? null : { mn: Math.floor(mn), mx: Math.ceil(mx) };
  }, [parcels, filters]);
  // Tri-state chips: click 1 = ONLY this value · click 2 = anything BUT it ("!v" in the set,
  // shown as "≠ value") · click 3 = neutral. Exclusions ride in the same Set so saved views,
  // ser/deser, counting, and the criteria bar all keep working unchanged.
  const toggle = (key: keyof FilterState, v: string) => {
    const s = new Set(filters[key] as Set<string>);
    if (s.has(v)) { s.delete(v); s.add('!' + v); }
    else if (s.has('!' + v)) { s.delete('!' + v); }
    else { s.add(v); }
    setFilters({ ...filters, [key]: s });
  };
  const tog = (key: keyof FilterState) => setFilters({ ...filters, [key]: !filters[key] } as FilterState);
  const num = (v: string) => { const n = parseInt(v.replace(/[^0-9]/g, ''), 10); return isNaN(n) ? null : n; };
  // Only the hottest section ('Phase & land') + the compact Block-&-lot lookup start open; everything
  // else collapses so first-paint fits on a screen instead of a mile of chips.
  const [closed, setClosed] = useState<Set<string>>(() => new Set([...SECTIONS.filter(s => !s.open).map(s => s.title), SITE_TITLE]));
  const [modelQuery, setModelQuery] = useState('');  // Model search box (filters across all subgroups)
  // Model subgroups collapse state — holds the EXPANDED land-use categories. Default = empty set =
  // every subgroup COLLAPSED. A subgroup also auto-opens while searching or when it holds a selection.
  const [modelCatOpen, setModelCatOpen] = useState<Set<string>>(() => new Set());
  // Map each model value → the Land-Use category it belongs to (the most common category among the
  // lots carrying that model), derived live from the data. Drives the model subgroup headers.
  const modelCat = useMemo(() => {
    const counts = new Map<string, Map<string, number>>();
    for (const p of parcels) {
      const cat = (SEL.landUse(p) || '').trim() || 'Other';
      for (const mod of tokens(SEL.model(p), 'model')) {
        if (!mod) continue;
        let cm = counts.get(mod); if (!cm) { cm = new Map(); counts.set(mod, cm); }
        cm.set(cat, (cm.get(cat) || 0) + 1);
      }
    }
    const out = new Map<string, string>();
    for (const [mod, cm] of counts) {
      let best = 'Other', bestN = -1;
      for (const [cat, n] of cm) if (n > bestN) { best = cat; bestN = n; }
      out.set(mod, best);
    }
    return out;
  }, [parcels]);
  const sectionCount = (keys: (keyof FilterState)[]) => keys.reduce((n, k) => n + (filters[k] as Set<string>).size, 0);
  // Zebra-stripe the filter sections (alternating subtle band) so they're easy to tell apart at a
  // glance — mirrors the right-panel units grid. A running counter over the RENDERED sections in
  // visual order (Block&lot → SECTIONS → Site), so adjacent VISIBLE sections always alternate even
  // when an empty section is skipped. Every section gets the same 8px inset so content stays aligned.
  let zebra = 0;
  const bandStyle = (): React.CSSProperties => ({ paddingLeft: 8, paddingRight: 8, ...(zebra++ % 2 ? { background: T.sand, borderRadius: 8 } : {}) });

  return (
    <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 10, marginTop: 4 }}>
      {/* Block # / Lot # — two SEPARATE exact-match number inputs (e.g. Block 6 · Lot 42). Lives at
          the TOP, next to "Find a parcel", so every "jump to a specific lot" affordance is together.
          Block 0–25 and most plat lots are ≤2 digits, so the inputs cap at 2. */}
      {(() => {
        const isOpen = !closed.has(BLOCKLOT_TITLE);
        const cnt = (filters.block != null ? 1 : 0) + (filters.lot != null ? 1 : 0);
        const cell = (label: string, key: 'block' | 'lot') => (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 3, fontWeight: 500 }}>{label}</div>
            <input key={`${key}-${filters[key] ?? ''}`} inputMode="numeric" maxLength={2} placeholder="—" defaultValue={filters[key] ?? ''}
              onBlur={e => setFilters({ ...filters, [key]: num(e.target.value) })} style={sfInput()} className="num" />
          </div>
        );
        return (
          <div style={{ marginBottom: 6, borderBottom: `1px solid ${T.sand2}`, paddingBottom: 6, ...bandStyle() }}>
            <button onClick={() => setClosed(prev => { const n = new Set(prev); n.has(BLOCKLOT_TITLE) ? n.delete(BLOCKLOT_TITLE) : n.add(BLOCKLOT_TITLE); return n; })}
              className="lfe-press" style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}>
              <span style={{ fontSize: 10, color: T.inkSoft, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s', pointerEvents: 'none', width: 12, textAlign: 'center', flex: '0 0 auto' }}>▶</span>
              <span className="eyebrow" style={{ margin: 0, pointerEvents: 'none' }}>{BLOCKLOT_TITLE}</span>
              {cnt > 0 && <span className="num" style={{ marginLeft: 'auto', fontSize: 10, color: T.onAccent, background: T.accent, borderRadius: 999, padding: '1px 7px' }}>{cnt}</span>}
            </button>
            {isOpen && <div style={{ display: 'flex', gap: 18, alignItems: 'flex-end', marginBottom: 4 }}>{cell('Block #', 'block')}{cell('Lot #', 'lot')}</div>}
          </div>
        );
      })()}
      {SECTIONS.map(sec => {
        const groups = sec.keys.map(k => ({ key: k, opts: vals(k as string) })).filter(g => g.opts.length);
        if (!groups.length) return null;
        const isOpen = !closed.has(sec.title);
        const cnt = sectionCount(sec.keys);
        return (
          <div key={sec.title} style={{ marginBottom: 6, borderBottom: `1px solid ${T.sand2}`, paddingBottom: 6, ...bandStyle() }}>
            <button onClick={() => setClosed(prev => { const n = new Set(prev); n.has(sec.title) ? n.delete(sec.title) : n.add(sec.title); return n; })}
              className="lfe-press" style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}>
              <span style={{ fontSize: 10, color: T.inkSoft, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s', pointerEvents: 'none', width: 12, textAlign: 'center', flex: '0 0 auto' }}>▶</span>
              <span className="eyebrow" style={{ margin: 0, pointerEvents: 'none' }}>{sec.title}</span>
              {cnt > 0 && <span className="num" style={{ marginLeft: 'auto', fontSize: 10, color: T.onAccent, background: T.accent, borderRadius: 999, padding: '1px 7px' }}>{cnt}</span>}
            </button>
            {isOpen && groups.map(g => (
              <div key={g.key as string} style={{ margin: '4px 0 8px' }}>
                {sec.subheads?.[g.key] &&
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: T.inkSoft, margin: '10px 0 5px', paddingTop: 8, borderTop: `1px solid ${T.sand2}` }}>{sec.subheads[g.key]}</div>}
                {groupLabel(g.key as string) !== sec.title &&
                  <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 3, fontWeight: 500 }}>{groupLabel(g.key as string)}</div>}
                {GROUP_HINT[g.key as string] &&
                  <div style={{ fontSize: 10, color: T.inkSoft, opacity: .8, marginBottom: 4, lineHeight: 1.35 }}>{GROUP_HINT[g.key as string]}</div>}
                {g.key === 'model' ? (() => {
                  // Flat, SEARCHABLE model list — the land-use grouping used to duplicate the
                  // "Category (from Land Use)" filter above, so it's gone. Selected models always
                  // stay visible (so an active filter isn't hidden by an unrelated search term).
                  const q = modelQuery.trim().toLowerCase();
                  const sel = filters.model as Set<string>;
                  const shown = g.opts.filter(o => !q || o.toLowerCase().includes(q) || sel.has(o) || sel.has('!' + o));
                  return (
                    <div>
                      <input value={modelQuery} onChange={e => setModelQuery(e.target.value)} placeholder={`Search ${g.opts.length} models…`}
                        style={{ width: '100%', padding: '5px 8px', border: `1px solid ${T.line}`, borderRadius: 7, background: T.surface, fontSize: 12, marginBottom: 5 }} />
                      {(() => {
                        // Group the (search-filtered) models by their Land-Use category — each a
                        // COLLAPSIBLE subgroup, default collapsed. Auto-opens while searching or when
                        // the subgroup holds an active selection so matches/filters stay visible.
                        if (!shown.length) return <span style={{ fontSize: 11, color: T.inkSoft }}>No models match “{modelQuery.trim()}”.</span>;
                        const byCat = new Map<string, string[]>();
                        for (const o of shown) { const c = modelCat.get(o) || 'Other'; (byCat.get(c) || byCat.set(c, []).get(c)!).push(o); }
                        const pri = PRIORITY.landUse || [];
                        const cats = [...byCat.keys()].sort((a, b) => {
                          const ka = pri.indexOf(a) < 0 ? pri.length : pri.indexOf(a);
                          const kb = pri.indexOf(b) < 0 ? pri.length : pri.indexOf(b);
                          return ka - kb || a.localeCompare(b);
                        });
                        return cats.map(cat => {
                          const models = byCat.get(cat)!;
                          const hasSel = models.some(o => sel.has(o) || sel.has('!' + o));
                          const open = !!q || hasSel || modelCatOpen.has(cat);
                          return (
                            <div key={cat} style={{ marginBottom: 1 }}>
                              <button onClick={() => setModelCatOpen(prev => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n; })}
                                className="lfe-press" style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0' }}>
                                <span style={{ fontSize: 9, color: T.inkSoft, width: 10, textAlign: 'center', flex: '0 0 auto', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', pointerEvents: 'none' }}>▶</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: hasSel ? T.accent : T.inkSoft, pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</span>
                                <span className="num" style={{ marginLeft: 'auto', fontSize: 10, color: T.inkSoft, pointerEvents: 'none' }}>{models.length}</span>
                              </button>
                              {open && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '2px 0 6px 16px' }}>
                                {models.map(o => { const ex = sel.has('!' + o); return (
                                  <Btn key={o} variant="chip" on={sel.has(o) || ex} onClick={() => toggle('model', o)}
                                    title={ex ? `Excluding ${o} — click to reset` : undefined}
                                    style={ex ? { background: T.amberTint, borderColor: T.maroon, color: T.maroon } : undefined}>{ex ? `≠ ${o}` : o}</Btn>
                                ); })}
                              </div>}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  );
                })() : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {g.opts.map(o => { const ex = (filters[g.key] as Set<string>).has('!' + o); return (
                      <Btn key={o} variant="chip" on={(filters[g.key] as Set<string>).has(o) || ex} onClick={() => toggle(g.key, o)}
                        title={ex ? `Excluding ${o} — click to reset` : undefined}
                        style={ex ? { background: T.amberTint, borderColor: T.maroon, color: T.maroon } : undefined}>{ex ? `≠ ${o}` : o}</Btn>
                    ); })}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}


      {(() => {
        const isOpen = !closed.has(SITE_TITLE);
        const cnt = (filters.sfMin != null || filters.sfMax != null ? 1 : 0)
          + (filters.lakeviewOnly ? 1 : 0) + (filters.sidewalkOnly ? 1 : 0) + (filters.easementOnly ? 1 : 0)
          + (filters.markersOnly ? 1 : 0) + (filters.onlyExceptions ? 1 : 0);
        return (
          <div style={{ marginBottom: 6, borderBottom: `1px solid ${T.sand2}`, paddingBottom: 6, ...bandStyle() }}>
            <button onClick={() => setClosed(prev => { const n = new Set(prev); n.has(SITE_TITLE) ? n.delete(SITE_TITLE) : n.add(SITE_TITLE); return n; })}
              className="lfe-press" style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}>
              <span style={{ fontSize: 10, color: T.inkSoft, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s', pointerEvents: 'none', width: 12, textAlign: 'center', flex: '0 0 auto' }}>▶</span>
              <span className="eyebrow" style={{ margin: 0, pointerEvents: 'none' }}>{SITE_TITLE}</span>
              {cnt > 0 && <span className="num" style={{ marginLeft: 'auto', fontSize: 10, color: T.onAccent, background: T.accent, borderRadius: 999, padding: '1px 7px' }}>{cnt}</span>}
            </button>
            {isOpen && <>
              <div style={{ fontSize: 11, color: T.inkSoft, margin: '4px 0 3px', fontWeight: 500 }}>Lot SF range</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                {/* key remounts the input when the committed value changes externally (e.g. "Clear
                    filters" / saved view) so the box can't show stale text; onBlur keeps commit-on-blur. */}
                <input key={`sfmin-${filters.sfMin ?? ''}`} inputMode="numeric" placeholder={sfBounds ? fmt(sfBounds.mn) : 'min'} defaultValue={filters.sfMin ?? ''}
                  onBlur={e => setFilters({ ...filters, sfMin: num(e.target.value) })} style={sfInput()} className="num" />
                <span style={{ color: T.inkSoft, fontSize: 12 }}>–</span>
                <input key={`sfmax-${filters.sfMax ?? ''}`} inputMode="numeric" placeholder={sfBounds ? fmt(sfBounds.mx) : 'max'} defaultValue={filters.sfMax ?? ''}
                  onBlur={e => setFilters({ ...filters, sfMax: num(e.target.value) })} style={sfInput()} className="num" />
              </div>
              <div style={{ fontSize: 11, color: T.inkSoft, margin: '4px 0 3px', fontWeight: 500 }}>Lot attributes</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                <Btn variant="chip" on={filters.lakeviewOnly} onClick={() => tog('lakeviewOnly')}>Lakeview</Btn>
                <Btn variant="chip" on={filters.sidewalkOnly} onClick={() => tog('sidewalkOnly')}>Sidewalk</Btn>
                <Btn variant="chip" on={filters.easementOnly} onClick={() => tog('easementOnly')}>Easement</Btn>
                <Btn variant="chip" on={filters.markersOnly} onClick={() => tog('markersOnly')}>Site markers</Btn>
                <Btn variant="chip" on={filters.onlyExceptions} onClick={() => tog('onlyExceptions')}
                  style={{ borderColor: T.amber, color: filters.onlyExceptions ? T.onAccent : T.amber, background: filters.onlyExceptions ? T.amber : T.surface }}>⚠ Exceptions</Btn>
              </div>
            </>}
          </div>
        );
      })()}
    </div>
  );
}
const SITE_TITLE = 'Site & attributes';
const BLOCKLOT_TITLE = 'Block & lot';

// chip style moved to ui/primitives Btn (variant="chip") — same pixels except vertical padding
// 4→5px (a11y hit-target bump; horizontal wrapping unchanged). aria-pressed comes with Btn.
const sfInput = (): React.CSSProperties => ({ flex: 1, minWidth: 0, padding: '6px 8px', border: `1px solid ${T.line}`, borderRadius: 7, background: T.surface, fontSize: 12 });
